import * as React from 'react'
import { Check } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { CardOnlyPayment, EventPaymentOptions } from '@/components/events/event-payment-options'
import type { EventCheckoutOrder } from '@/components/events/public-event-receipt'
import { AttendeeNameFields, BuyerFields } from '@/components/events/public-event-buyer-fields'
import { RegistrationQuestionFields } from '@/components/events/public-registration-questions'
import { usePublicEventRegistration } from '@/components/events/use-public-event-registration'
import type { VerifiedEventBuyer } from '@/components/events/member-ticket-verification'
import { apiMessage } from '@/lib/contacts/errors'

export function EventCheckoutForm({
  formId,
  attendeeLabels,
  memberVerification,
  requiresPayment,
  registration,
  payLaterAvailable,
  paymentMethod,
  onPaymentMethodChange,
  payLaterDueOn,
  onPayLaterOrder,
}: {
  formId: string
  attendeeLabels: string[]
  memberVerification: VerifiedEventBuyer | null
  requiresPayment: boolean
  registration: ReturnType<typeof usePublicEventRegistration>
  payLaterAvailable: boolean
  paymentMethod: 'online' | 'invoice'
  onPaymentMethodChange: (value: 'online' | 'invoice') => void
  payLaterDueOn?: string
  onPayLaterOrder: (order: EventCheckoutOrder) => void
}) {
  const registrationFields = useRegistrationFields(registration)
  return (
    <form
      id={formId}
      className="divide-y divide-border"
      onSubmit={(formEvent) => {
        formEvent.preventDefault()
        registrationFields.register.mutate(undefined, {
          onSuccess: (result) => {
            if (result.checkout_session)
              window.location.assign(result.checkout_session.checkout_url)
            if (paymentMethod === 'invoice' && result.order) onPayLaterOrder(result.order)
          },
        })
      }}
    >
      {registrationFields.register.error && (
        <Alert className="m-4">{apiMessage(registrationFields.register.error)}</Alert>
      )}
      <CheckoutSection
        title="Your details"
        notice={
          memberVerification ? (
            <span className="flex items-center gap-1.5 text-caption text-muted-foreground">
              <Check className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
              Verified member · details prefilled
            </span>
          ) : undefined
        }
      >
        <BuyerFields {...registrationFields.buyerFields} />
      </CheckoutSection>
      <CheckoutSection title="Attendees" caption="Names can be added later from your order link.">
        <AttendeeNameFields
          attendeeLabels={attendeeLabels}
          {...registrationFields.attendeeFields}
        />
      </CheckoutSection>
      <RegistrationQuestionFields
        attendeeLabels={attendeeLabels}
        {...registrationFields.questions}
      />
      {requiresPayment && (
        <section className="px-4 py-4">
          {payLaterAvailable ? (
            <EventPaymentOptions
              value={paymentMethod}
              dueOn={payLaterDueOn}
              onChange={onPaymentMethodChange}
            />
          ) : (
            <CardOnlyPayment />
          )}
        </section>
      )}
      <Input
        type="text"
        name="website"
        value={registrationFields.website}
        autoComplete="off"
        tabIndex={-1}
        aria-hidden="true"
        className="hidden"
        onChange={(event) => registrationFields.setWebsite(event.target.value)}
      />
      {registrationFields.register.isPending && (
        <RegistrationPending paymentMethod={paymentMethod} requiresPayment={requiresPayment} />
      )}
    </form>
  )
}

function useRegistrationFields(registration: ReturnType<typeof usePublicEventRegistration>) {
  return {
    register: registration.register,
    website: registration.website,
    setWebsite: registration.setWebsite,
    buyerFields: {
      buyerName: registration.buyerName,
      buyerEmail: registration.buyerEmail,
      setBuyerName: registration.setBuyerName,
      setBuyerEmail: registration.setBuyerEmail,
    },
    attendeeFields: { attendees: registration.attendees, setAttendees: registration.setAttendees },
    questions: {
      questions: registration.orderQuestions,
      answers: registration.answers,
      onAnswer: (questionId: number, value: string | string[]) =>
        registration.setAnswers((current) => ({ ...current, [questionId]: value })),
      attendeeQuestions: registration.attendeeQuestions,
      attendeeAnswers: registration.attendeeAnswers,
      onAttendeeAnswer: (attendeeIndex: number, questionId: number, value: string | string[]) =>
        registration.setAttendeeAnswers((current) =>
          current.map((answers, index) =>
            index === attendeeIndex ? { ...answers, [questionId]: value } : answers,
          ),
        ),
    },
  }
}

function RegistrationPending({
  paymentMethod,
  requiresPayment,
}: {
  paymentMethod: 'online' | 'invoice'
  requiresPayment: boolean
}) {
  return (
    <p className="px-4 py-3 text-caption text-muted-foreground" role="status">
      {paymentMethod === 'invoice'
        ? 'Confirming registration…'
        : requiresPayment
          ? 'Starting secure checkout…'
          : 'Registering…'}
    </p>
  )
}

function CheckoutSection({
  title,
  caption,
  notice,
  children,
}: {
  title: string
  caption?: string
  notice?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="space-y-3 px-4 py-4">
      <div>
        <h2 className="text-body-sm font-medium">{title}</h2>
        {caption && <p className="mt-0.5 text-caption text-muted-foreground">{caption}</p>}
        {notice}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  )
}
